import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { ReactNode } from "react";

/**
 * A grey preview stage with a white name bar under it, filling its column.
 * Controls are neutral grey; blue is left to state elsewhere on the sheet
 * (Design Studio actor-settings-layout/d-appearance*).
 *
 * Without a `preview` the stage explains what happens instead (`empty`).
 * `onClear` adds the ✕; leave it out when nothing is overridden.
 */
export default function PiPreviewStage(props: {
  preview?: ReactNode;
  empty?: { icon: ReactNode; title: string; body?: string };
  name: string;
  /** Quiet note after the name, e.g. "from the preset". */
  meta?: string;
  actionLabel: string;
  onAction: () => void;
  onClear?: () => void;
  clearLabel?: string;
  disabled?: boolean;
  testIds?: { stage?: string; empty?: string; action?: string; clear?: string };
}) {
  const { preview, empty, name, meta, actionLabel, onAction, onClear, clearLabel, disabled, testIds } = props;

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <div
        className="relative flex min-h-[200px] flex-1 items-center justify-center overflow-hidden rounded-t-[8px] bg-creator-surfaceHover"
        data-testid={testIds?.stage}
      >
        {preview ??
          (empty && (
            <div
              className="flex max-w-[280px] flex-col items-center gap-[8px] px-[16px] text-center text-creator-text-sub"
              role="status"
              data-testid={testIds?.empty}
            >
              <span className="mb-[4px] flex text-[36px] text-creator-text-soft" aria-hidden="true">
                {empty.icon}
              </span>
              <span className="text-[16px] font-semibold">{empty.title}</span>
              {empty.body && <span className="text-[13px] leading-[1.4]">{empty.body}</span>}
            </div>
          ))}
        {onClear && !disabled && (
          <button
            type="button"
            aria-label={clearLabel}
            title={clearLabel}
            className="absolute right-[12px] top-[12px] flex h-[28px] w-[28px] cursor-pointer items-center justify-center rounded-full border-0 bg-creator-surface p-0 text-[16px] text-creator-text-sub shadow-[0_1px_3px_rgba(16,24,40,0.12)] transition-colors hover:bg-creator-surfaceHover focus:outline-none focus-visible:ring-2 focus-visible:ring-creator-primary"
            onClick={onClear}
            data-testid={testIds?.clear}
          >
            <CloseIcon fontSize="inherit" color="inherit" />
          </button>
        )}
      </div>
      <div className="box-border flex h-[64px] flex-none items-center gap-[12px] rounded-b-[8px] border border-solid border-creator-border bg-creator-surface px-[16px]">
        <span className="flex min-w-0 flex-1 items-baseline gap-[8px] text-creator-text-sub">
          <span className="truncate text-[16px] font-semibold">{name}</span>
          {meta && <span className="truncate text-[12px]">{meta}</span>}
        </span>
        <button
          type="button"
          disabled={disabled}
          className="box-border flex h-[36px] flex-none cursor-pointer items-center gap-[6px] rounded-[6px] border border-solid border-creator-border bg-creator-surface px-[12px] font-[inherit] text-[13px] font-medium text-creator-text-sub transition-colors hover:bg-creator-surfaceHover focus:outline-none focus-visible:ring-2 focus-visible:ring-creator-primary disabled:cursor-default disabled:opacity-50"
          onClick={onAction}
          data-testid={testIds?.action}
        >
          <SwapHorizIcon fontSize="inherit" className="text-[16px]" />
          {actionLabel}
        </button>
      </div>
    </div>
  );
}
